import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useState } from 'react';

import type { ReferentMutation } from '../../../@types/Company';
import type { ReferentSmtpConfigData, SmtpFormData } from '../../../@types/MailConfig';
import Spinner from '../../common/Spinner';
import SmtpTutorial from './SmtpTutorial';

interface SmtpConfigCardProps {
  referent: ReferentMutation;
  config?: ReferentSmtpConfigData;
  onSave: (referentId: string, data: SmtpFormData) => Promise<void>;
  onTest: (referentId: string) => Promise<{ success: boolean; message: string }>;
}

export default function SmtpConfigCard({ referent, config, onSave, onTest }: SmtpConfigCardProps) {
  const [form, setForm] = useState<SmtpFormData>({
    smtpHost: config?.smtpHost ?? '',
    smtpPort: config?.smtpPort ?? 587,
    smtpEncryption: (config?.smtpEncryption as SmtpFormData['smtpEncryption']) ?? 'tls',
    smtpUsername: config?.smtpUsername ?? '',
    smtpPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showTutorial, setShowTutorial] = useState(false);

  const isConfigured = Boolean(config);
  const isVerified = config?.isVerified ?? false;

  const handleSave = useCallback(async() => {
    if (!form.smtpHost || !form.smtpUsername) { return; }
    setSaving(true);
    setTestResult(null);
    try {
      await onSave(referent.id, form);
    } finally {
      setSaving(false);
    }
  }, [form, referent.id, onSave]);

  const handleTest = useCallback(async() => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await onTest(referent.id);
      setTestResult(result);
    } finally {
      setTesting(false);
    }
  }, [referent.id, onTest]);

  const applyPreset = useCallback((preset: { host: string; port: number; encryption: 'tls' | 'ssl' }) => {
    setForm(prev => ({
      ...prev,
      smtpHost: preset.host,
      smtpPort: preset.port,
      smtpEncryption: preset.encryption,
    }));
    setShowTutorial(false);
  }, []);

  return (
    <div className={`rounded-xl border-2 transition-colors ${
      isVerified ? 'border-emerald-300 bg-emerald-50/30' : isConfigured ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200 bg-white'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between border-b px-5 py-4" style={{ borderColor: 'inherit' }}>
        <div className="flex items-center gap-3">
          {referent.photoName ? (
            <img
              src={referent.photoName}
              alt={`${referent.firstname} ${referent.lastname}`}
              className="h-10 w-10 rounded-full border-2 border-white object-cover shadow"
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-indigo-400 to-blue-600 text-sm font-bold text-white shadow">
              {referent.firstname?.[0]}{referent.lastname?.[0]}
            </div>
          )}
          <div>
            <h4 className="font-semibold text-gray-900">{referent.title} {referent.firstname} {referent.lastname}</h4>
            {referent.email && <p className="text-xs text-gray-500">{referent.email}</p>}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isVerified && (
            <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
              <CheckCircleIcon className="h-4 w-4" /> Vérifié
            </span>
          )}
          {isConfigured && !isVerified && (
            <span className="flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
              <ExclamationTriangleIcon className="h-4 w-4" /> Non vérifié
            </span>
          )}
          {!isConfigured && (
            <span className="flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
              Non configuré
            </span>
          )}
        </div>
      </div>

      {/* Form */}
      <div className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <h5 className="text-sm font-semibold text-gray-700">Paramètres SMTP</h5>
          <button
            type="button"
            onClick={() => setShowTutorial(!showTutorial)}
            className="text-sm font-medium text-rgpd-blue transition-colors hover:text-rgpd-blue-dark"
          >
            {showTutorial ? 'Masquer le guide' : 'Guide de configuration'}
          </button>
        </div>

        {showTutorial && (
          <div className="mb-5">
            <SmtpTutorial onApplyPreset={applyPreset} />
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Serveur SMTP *</label>
            <input
              type="text"
              value={form.smtpHost}
              onChange={(e) => setForm(p => ({ ...p, smtpHost: e.target.value }))}
              placeholder="smtp.gmail.com"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm transition-colors focus:border-rgpd-blue focus:ring-1 focus:ring-rgpd-blue focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Port (SMTP) *</label>
            <input
              type="number"
              value={form.smtpPort}
              onChange={(e) => setForm(p => ({ ...p, smtpPort: parseInt(e.target.value, 10) || 587 }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm transition-colors focus:border-rgpd-blue focus:ring-1 focus:ring-rgpd-blue focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Chiffrement</label>
            <select
              value={form.smtpEncryption}
              onChange={(e) => setForm(p => ({ ...p, smtpEncryption: e.target.value as SmtpFormData['smtpEncryption'] }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm transition-colors focus:border-rgpd-blue focus:ring-1 focus:ring-rgpd-blue focus:outline-none"
            >
              <option value="tls">TLS (recommandé)</option>
              <option value="ssl">SSL</option>
              <option value="">Aucun</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Nom d'utilisateur / Email *</label>
            <input
              type="text"
              value={form.smtpUsername}
              onChange={(e) => setForm(p => ({ ...p, smtpUsername: e.target.value }))}
              placeholder="votre@email.com"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm transition-colors focus:border-rgpd-blue focus:ring-1 focus:ring-rgpd-blue focus:outline-none"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Mot de passe / Mot de passe d'application *
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.smtpPassword}
                onChange={(e) => setForm(p => ({ ...p, smtpPassword: e.target.value }))}
                placeholder={config ? '••••••••  (laisser vide pour conserver l\'existant)' : 'Mot de passe SMTP'}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 text-sm transition-colors focus:border-rgpd-blue focus:ring-1 focus:ring-rgpd-blue focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute top-1/2 right-2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        {testResult && (
          <div className={`mt-4 rounded-lg p-3 text-sm ${
            testResult.success 
              ? 'border border-emerald-200 bg-emerald-50 text-emerald-800' 
              : 'border border-red-200 bg-red-50 text-red-800'
          }`}>
            {testResult.success ? (
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="h-5 w-5 text-emerald-600" />
                {testResult.message}
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                <span>{testResult.message}</span>
              </div>
            )}
          </div>
        )}

        <div className="mt-5 flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !form.smtpHost || !form.smtpUsername}
            className="flex items-center gap-2 rounded-lg bg-rgpd-blue px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rgpd-blue-dark disabled:opacity-50"
          >
            {saving ? <><Spinner size="sm" /> Enregistrement...</> : 'Enregistrer'}
          </button>

          {isConfigured && (
            <button
              type="button"
              onClick={handleTest}
              disabled={testing}
              className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 transition-colors hover:bg-emerald-100 disabled:opacity-50"
            >
              {testing ? (
                <><Spinner size="sm" /> Test en cours...</>
              ) : (
                <><PaperAirplaneIcon className="h-4 w-4" /> Tester la connexion</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
